import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import LocalImage from '../LocalImage';
import { InstagramQueryPostItemModel } from '@/types/anonymously.interface';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { formatNumberToK } from '@/shared/utils';
import { useUserProvider } from '@/context/UserProvider';
import { getInsStoryDownload } from '@/lib/service/dashboard';
import { message } from 'antd';
import { LimitAlert } from '@/components/LandingPage/FeeLimitAlert';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ArrowButton = styled.button<{ position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.position === 'left' ? 'left: 0;' : 'right: 0;')} /* 根据位置动态设置 */
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: white;
  &:hover {
    color: gray;
  }
`;

const MediaContainer = styled.div<{ scale: number }>`
  width: 80%;
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(${(props) => props.scale});
  transition: transform 0.3s ease-in-out;
  cursor: zoom-in;
  border-radius: 18px;
  overflow: hidden;
`;

const StyledVideo = styled.video`
  width: 100%;
  aspect-ratio: 9/16;
  min-width: 300px;
  min-height: 400px;
  object-fit: contain;
`;

const StyledImage = styled(LocalImage)`
  width: 100%;
  border-radius: 18px;
  aspect-ratio: 9/16;
  min-width: 300px;
  min-height: 400px;
  object-fit: contain;
`;

const ImageCaption = styled.div`
  color: white;
  font-size: 1rem;
  padding: 5px 10px;
`;

// const CloseButton = styled(CloseOutlined)`
//   position: absolute;
//   top: 10px;
//   right: 10px;
//   font-size: 24px;
//   cursor: pointer;
//   color: white;

//   &:hover {
//     color: gray;
//   }
// `;

interface ImageGalleryProps {
  dataList: InstagramQueryPostItemModel[];
  index?: number;
  show?: boolean;
  onClose: () => void;
  gotoPlanClick?: () => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ dataList, index = 0, show = false, onClose, gotoPlanClick }) => {
  const [currentIndex, setCurrentIndex] = useState(index);
  const [currentModel, setCurrentModel] = useState<InstagramQueryPostItemModel>(null);
  const [scale, setScale] = useState(1);
  const [currentSubIndex, setCurrentSubIndex] = useState(0);
  const { t } = useTranslation();
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showLimit, setShowLimit] = useState(false);

  useEffect(() => {
    setCurrentSubIndex(0);
    setCurrentIndex(index);
  }, [index]);

  useEffect(() => {
    if (dataList?.length > currentIndex) {
      setCurrentModel(dataList[currentIndex]);
    } else {
      setCurrentModel(null);
    }
  }, [currentIndex, dataList]);

  const handlePrevious = () => {
    if (dataList?.length > currentIndex) {
      const tempData = dataList[currentIndex];

      if (isEmpty(tempData?.media_list) || tempData?.media_list?.length === 0) {
        setCurrentSubIndex(0);
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? dataList.length - 1 : prevIndex - 1));
      } else {
        if (currentIndex === 0 && currentSubIndex === 0) {
          return;
        }
        if (tempData?.media_list?.length > 0) {
          if (currentSubIndex === 0) {
            const preIndex = currentIndex === 0 ? dataList.length - 1 : currentIndex - 1;
            const preData = dataList[preIndex];
            if (preData?.media_list?.length > 0) {
              setCurrentSubIndex(preData?.media_list?.length - 1);
            }
            setCurrentIndex((prevIndex) => (prevIndex === 0 ? dataList.length - 1 : prevIndex - 1));
          } else {
            setCurrentSubIndex((prevIndex) => prevIndex - 1);
          }
        }
      }
    } else {
      setCurrentModel(null);
    }

    setScale(1); // 重置缩放
  };

  const handleNext = () => {
    if (dataList?.length > currentIndex) {
      const tempData = dataList[currentIndex];

      if (isEmpty(tempData?.media_list) || tempData?.media_list?.length === 0) {
        setCurrentSubIndex(0);
        setCurrentIndex((prevIndex) => (prevIndex === dataList.length - 1 ? 0 : prevIndex + 1));
      } else {
        if (tempData?.media_list?.length > 0) {
          if (currentSubIndex === tempData?.media_list?.length - 1) {
            setCurrentSubIndex(0);
            setCurrentIndex((prevIndex) => (prevIndex === dataList.length - 1 ? 0 : prevIndex + 1));
          } else {
            setCurrentSubIndex((prevIndex) => prevIndex + 1);
          }
        }
      }
    } else {
      setCurrentModel(null);
    }
    setScale(1); // 重置缩放
  };

  const onDownloadClick = (postItemModel: InstagramQueryPostItemModel) => {
    if (!isLogin) {
      onChangedLoginView(false);
      onOpenLoginModal(true);
      return;
    }
    if (downloadLoading) {
      return;
    }
    setDownloadLoading(false);

    getInsStoryDownload(postItemModel?.media_name, 'single', null)
      .then((res) => {
        if (res?.code === 0) {
          handleDownload(postItemModel);
          return;
        }
        if (res?.code === 22010 || res?.code === 22011) {
          setShowLimit(true);
          return;
        } else {
          message.error(res?.message);
        }
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const handleDownload = async (postItemModel: InstagramQueryPostItemModel) => {
    if (!postItemModel) {
      return;
    }

    let imgUrlStr;
    const currentMedia = postItemModel?.media_list[currentSubIndex];
    if (postItemModel?.media_list?.length > 0) {
      imgUrlStr = currentMedia?.media_url;
    } else {
      imgUrlStr = postItemModel?.cover_image;
    }

    if (!imgUrlStr) return;

    try {
      const response = await fetch(`/n-api/download-image?url=${encodeURIComponent(imgUrlStr)}`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      if (currentMedia?.media_type === 2) {
        a.download = `Dolphin_Radar_${dayjs().format('YYYYMMDDHHmmss')}.mp4`;
      } else {
        a.download = `Dolphin_Radar_${dayjs().format('YYYYMMDDHHmmss')}.png`;
      }
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <Container style={{ display: show ? 'flex' : 'none' }}>
      <div className={styles.content}>
        <CloseOutlined className={styles.closeBtn} onClick={onClose} />

        <div className={styles.topContainer}>
          {currentModel?.publish_time && <p>{currentModel?.publish_time}</p>}

          <div className={styles.infoDataContainer}>
            <Image alt="" src={'/images/toolkit/comment-icon.webp'} width={20} height={20} loading="lazy" />
            <span style={{ marginRight: '10px' }}>{formatNumberToK(currentModel?.comment_count)}</span>
            <Image alt="" src={'/images/toolkit/like-icon.webp'} width={20} height={20} loading="lazy" />
            <span>{formatNumberToK(currentModel?.like_count)}</span>
          </div>

          <span className={styles.download} onClick={() => onDownloadClick(currentModel)}>
            {t('Download')}
          </span>
        </div>

        <ImageWrapper>
          <ArrowButton position="left" onClick={handlePrevious}>
            <LeftOutlined />
          </ArrowButton>

          <MediaContainer scale={scale}>
            {currentModel?.media_list ? (
              currentModel?.media_list[currentSubIndex]?.media_type == 2 ? (
                <StyledVideo
                  src={currentModel?.media_list[currentSubIndex]?.media_url}
                  controls
                  autoPlay
                  loop={false}
                  onEnded={handleNext}
                />
              ) : (
                <StyledImage alt="" src={currentModel?.media_list[currentSubIndex]?.media_url} />
              )
            ) : (
              <StyledImage alt="" src={currentModel?.cover_image} />
            )}
          </MediaContainer>

          {currentModel && (
            <ImageCaption>
              {currentModel?.caption && (
                <p className={styles.caption}>
                  {typeof currentModel?.caption === 'string' ? currentModel?.caption : currentModel?.caption?.text}
                </p>
              )}
            </ImageCaption>
          )}

          <ArrowButton position="right" onClick={handleNext}>
            <RightOutlined />
          </ArrowButton>
        </ImageWrapper>
      </div>

      <LimitAlert
        open={showLimit}
        closeAction={(isOk) => {
          setShowLimit(false);

          if (isOk) {
            setTimeout(() => {
              onClose && onClose();
              gotoPlanClick && gotoPlanClick();
              const dom = document.getElementById('1');
              dom && dom.scrollIntoView({ behavior: 'smooth' });
            }, 20);
          }
        }}
      />
    </Container>
  );
};

export default ImageGallery;
